import {
  OUTREACH_TERMINAL_STAGES,
  OutreachStageSchema,
  type OutreachProspect,
  type OutreachStage,
} from "@lumika/shared";
import { STAGE_ORDER } from "../outreach-ui";

const TERMINAL_SET = new Set<string>(OUTREACH_TERMINAL_STAGES);

/**
 * Group a flat prospect list into 6 stage buckets in funnel order.
 * Prospects with an unknown stage are silently dropped (no DB CHECK exists).
 */
export function groupByStage(
  prospects: OutreachProspect[],
): Record<OutreachStage, OutreachProspect[]> {
  const buckets = Object.fromEntries(
    STAGE_ORDER.map((s) => [s, [] as OutreachProspect[]]),
  ) as Record<OutreachStage, OutreachProspect[]>;

  for (const p of prospects) {
    const parsed = OutreachStageSchema.safeParse(p.stage);
    if (parsed.success) {
      buckets[parsed.data].push(p);
    }
  }

  return buckets;
}

/**
 * Sort a column's prospects for display priority:
 * 1. Overdue recontact (non-terminal, nextFollowUpAt in the past) — most urgent
 * 2. Then by soonest demoAt or nextFollowUpAt (null sinks to bottom)
 * 3. Then most recently updated
 *
 * Why: an overdue follow-up is more actionable than a future demo and
 * must surface above it so the admin can act immediately.
 */
export function sortColumn(
  prospects: OutreachProspect[],
  now: number = Date.now(),
): OutreachProspect[] {
  return [...prospects].sort((a, b) => {
    const aOverdue = isRecontactOverdue(a, now);
    const bOverdue = isRecontactOverdue(b, now);

    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

    // Soonest upcoming event (demo or recontact), null last.
    const aNext = nextEventMs(a, now);
    const bNext = nextEventMs(b, now);

    if (aNext !== null && bNext !== null) return aNext - bNext;
    if (aNext !== null) return -1;
    if (bNext !== null) return 1;

    // Both null — fall back to most recently updated.
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

function isRecontactOverdue(p: OutreachProspect, now: number): boolean {
  if (TERMINAL_SET.has(p.stage)) return false;
  if (!p.nextFollowUpAt) return false;
  return new Date(p.nextFollowUpAt).getTime() <= now;
}

/**
 * Earliest of demoAt / nextFollowUpAt that is still in the future, or null.
 * Past dates are excluded so a stale demo/follow-up (e.g. on a terminal or
 * already-handled prospect) can't outrank a prospect with a genuine upcoming
 * event — those fall through to the recency tie-break instead.
 */
function nextEventMs(p: OutreachProspect, now: number): number | null {
  const candidates: number[] = [];
  if (p.demoAt) {
    const t = new Date(p.demoAt).getTime();
    if (t > now) candidates.push(t);
  }
  if (p.nextFollowUpAt) {
    const t = new Date(p.nextFollowUpAt).getTime();
    if (t > now) candidates.push(t);
  }
  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

/** Initials from a display name: up to 2 chars from word starts. */
export function initials(name: string | null | undefined): string {
  if (!name?.trim()) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/**
 * Strip a phone string down to digits, +, (, ), -, space — the set
 * Chrome accepts in a tel: href. Returns null for blank input.
 */
export function safePhoneHref(phone: string | null): string | null {
  if (!phone?.trim()) return null;
  const dialable = phone.replace(/[^\d+\-(). ]/g, "");
  return dialable ? `tel:${dialable}` : null;
}
